import React, { useState, useMemo } from 'react';
import { Truck, Package, CheckCircle, AlertCircle, MapPin, Calendar, Search, X, User, ArrowUpDown } from 'lucide-react';

const STATUS_CONFIG = {
  '待确认': { color: 'bg-orange-100 text-orange-600', dot: 'bg-orange-500', icon: AlertCircle },
  '进行中': { color: 'bg-blue-100 text-blue-600', dot: 'bg-blue-500', icon: Truck },
  '已完成': { color: 'bg-green-100 text-green-600', dot: 'bg-green-500', icon: CheckCircle },
};

const SUB_STATUS_CONFIG = {
  '待送货': { color: 'text-slate-500' },
  '送货中': { color: 'text-blue-500' },
  '待结算': { color: 'text-orange-500' },
  '已结算': { color: 'text-amber-500' },
  '已开票': { color: 'text-teal-500' },
};

const StatusBadge = ({ status, subStatus }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG['进行中'];
  const subConfig = subStatus ? SUB_STATUS_CONFIG[subStatus] : null;
  
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-bold ${config.color}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
        {status}
      </span>
      {subConfig && (
        <span className={`text-xs font-medium ${subConfig.color}`}>
          · {subStatus}
        </span>
      )}
    </div>
  );
};

const ProgressBar = ({ progress, deliveries, showLegend = false }) => {
  const calculatedProgress = useMemo(() => {
    if (deliveries && deliveries.length > 0) {
      return {
        total: deliveries.length,
        waiting: deliveries.filter(d => d.status === '待送货').length,
        arrived: deliveries.filter(d => d.status === '已到场').length,
        stored: deliveries.filter(d => d.status === '已入库').length,
        needConfirm: deliveries.filter(d => d.status === '待结算').length,
        settled: deliveries.filter(d => d.status === '已结算').length,
        invoiced: deliveries.filter(d => d.status === '已开票').length,
        paid: deliveries.filter(d => d.status === '已付款').length,
      };
    }
    return {
      total: progress?.total || 0,
      waiting: progress?.waiting || 0,
      arrived: progress?.arrived || 0,
      stored: progress?.stored || 0,
      needConfirm: progress?.needConfirm || 0,
      settled: progress?.settled || 0,
      invoiced: progress?.invoiced || 0,
      paid: progress?.paid || 0,
    };
  }, [deliveries, progress]);

  const safeProgress = calculatedProgress;
  const completedCount = safeProgress.arrived + safeProgress.stored + safeProgress.needConfirm + safeProgress.settled + safeProgress.invoiced + safeProgress.paid;
  const percentage = safeProgress.total > 0 ? Math.round(completedCount / safeProgress.total * 100) : 0;
  
  return (
    <div className="mt-3">
      <div className="flex justify-between text-xs text-slate-500 mb-1.5">
        <span>送货进度 {completedCount}/{safeProgress.total}</span>
        <span className="font-medium">{percentage}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
        {safeProgress.waiting > 0 && (
          <div className="bg-slate-300 h-full" style={{ width: `${safeProgress.waiting / safeProgress.total * 100}%` }} />
        )}
        {safeProgress.arrived > 0 && (
          <div className="bg-blue-400 h-full" style={{ width: `${safeProgress.arrived / safeProgress.total * 100}%` }} />
        )}
        {safeProgress.stored > 0 && (
          <div className="bg-purple-400 h-full" style={{ width: `${safeProgress.stored / safeProgress.total * 100}%` }} />
        )}
        {safeProgress.needConfirm > 0 && (
          <div className="bg-orange-400 h-full animate-pulse" style={{ width: `${safeProgress.needConfirm / safeProgress.total * 100}%` }} />
        )}
        {safeProgress.settled > 0 && (
          <div className="bg-amber-400 h-full" style={{ width: `${safeProgress.settled / safeProgress.total * 100}%` }} />
        )}
        {safeProgress.invoiced > 0 && (
          <div className="bg-teal-400 h-full" style={{ width: `${safeProgress.invoiced / safeProgress.total * 100}%` }} />
        )}
        {safeProgress.paid > 0 && (
          <div className="bg-green-400 h-full" style={{ width: `${safeProgress.paid / safeProgress.total * 100}%` }} />
        )}
      </div>
      {showLegend && (
        <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-slate-500">
          {safeProgress.waiting > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300" />待送货 {safeProgress.waiting}</span>}
          {safeProgress.arrived > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" />已到场 {safeProgress.arrived}</span>}
          {safeProgress.stored > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400" />已入库 {safeProgress.stored}</span>}
          {safeProgress.needConfirm > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />待结算 {safeProgress.needConfirm}</span>}
          {safeProgress.settled > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />已结算 {safeProgress.settled}</span>}
          {safeProgress.invoiced > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-400" />已开票 {safeProgress.invoiced}</span>}
          {safeProgress.paid > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" />已付款 {safeProgress.paid}</span>}
        </div>
      )}
    </div>
  );
};

export default function OrderListPage({ orders, onOrderClick, onPrototypeClick }) {
  const [activeTab, setActiveTab] = useState('全部');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [sortOrder, setSortOrder] = useState('time'); // 'time' | 'amount'
  
  const tabs = ['全部', '待确认', '进行中', '待结算', '已完成'];
  
  const getSubStatus = (order) => {
    return null;
  };

  const filteredOrders = useMemo(() => {
    let result = [...orders];
    
    if (activeTab !== '全部') {
      if (activeTab === '待结算') {
        result = result.filter(order => 
          order.deliveries?.some(d => d.status === '待结算')
        );
      } else {
        result = result.filter(order => order.status === activeTab);
      }
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(order => 
        order.orderNo.toLowerCase().includes(query) ||
        order.baseName.toLowerCase().includes(query) ||
        (order.supplierInfo?.name && order.supplierInfo.name.toLowerCase().includes(query))
      );
    }
    
    if (sortOrder === 'amount') {
      result.sort((a, b) => (b.estimatedAmount || 0) - (a.estimatedAmount || 0));
    } else {
      result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    
    return result;
  }, [orders, activeTab, searchQuery, sortOrder]);

  return (
    <div className="min-h-full bg-slate-50">
      {/* 页面头部 */}
      <div className="bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-xl font-bold text-slate-800">我的订单</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
            >
              <Search size={20} />
            </button>
            <button
              onClick={() => setSortOrder(sortOrder === 'time' ? 'amount' : 'time')}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
              title={sortOrder === 'time' ? '按时间排序' : '按金额排序'}
            >
              <ArrowUpDown size={20} />
            </button>
          </div>
        </div>

        {/* 搜索框 */}
        {showSearch && (
          <div className="px-4 pb-3">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索订单号、基地名称、实供人..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2.5 pl-10 bg-slate-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all"
                autoFocus
              />
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full bg-slate-200 text-slate-500 hover:bg-slate-300"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tab切换 */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar px-4 pb-3">
          {tabs.map((tab) => {
            const count = tab === '待结算'
              ? orders.filter(o => o.deliveries?.some(d => d.status === '待结算')).length
              : orders.filter(o => o.status === tab).length;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all relative ${
                  activeTab === tab
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {tab}
                {tab !== '全部' && (
                  <span className="ml-1.5 text-xs opacity-80">
                    ({count})
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 space-y-3">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <Package size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">暂无{activeTab === '全部' ? '' : activeTab}订单</p>
            {searchQuery && <p className="text-xs text-slate-400 mt-1">尝试其他搜索条件</p>}
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div
              key={order.id}
              onClick={() => onOrderClick(order)}
              className="bg-white p-4 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-all border border-slate-200 hover:border-blue-200"
            >
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-mono">{order.orderNo}</span>
                  {order.mode === 'other' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded font-medium">
                      帮卖
                    </span>
                  )}
                  {order.demandType === 'closed' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded font-medium">
                      闭口
                    </span>
                  )}
                </div>
                <StatusBadge status={order.status} subStatus={getSubStatus(order)} />
              </div>
              
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                  {order.baseName?.charAt(0) || '基'}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-800 text-base truncate">{order.baseName}</h3>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                    <MapPin size={12} />
                    <span className="truncate">{order.baseAddress}</span>
                  </div>
                  {order.mode === 'other' && order.supplierInfo && (
                    <div className="flex items-center gap-1 text-xs text-purple-600 mt-1">
                      <User size={12} />
                      <span>实供人: {order.supplierInfo.name} ({order.supplierInfo.phone})</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-slate-500">物料类型</span>
                  <span className="text-sm text-slate-800 font-medium">{order.material}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-slate-500">预估重量</span>
                  <span className="text-sm text-slate-800 font-bold">{order.estimatedWeight} 吨</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">实际重量</span>
                  <span className="text-sm text-slate-800 font-bold">
                    {order.actualWeight ? `${order.actualWeight} 吨` : '待入库'}
                  </span>
                </div>
              </div>

              <ProgressBar progress={order.progress} deliveries={order.deliveries} showLegend={true} />

              <div className="flex justify-between items-center pt-3 mt-3 border-t border-slate-100">
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Calendar size={12} />
                  <span>{order.createdAt}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">预估金额</span>
                  <span className="text-lg font-bold text-red-600">¥{order.estimatedAmount?.toLocaleString()}</span>
                </div>
              </div>
              
              {order.status !== '已完成' && order.status !== '待确认' && (
                <div className="pt-3 mt-3 border-t border-slate-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOrderClick(order, true);
                    }}
                    className="w-full py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Truck size={16} />
                    预约发货
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* 原型选择按钮及弹窗 - 已注释 */}
      {/* {onPrototypeClick && (
        <>
          <button
            onClick={() => setShowPrototypeMenu(!showPrototypeMenu)}
            className="fixed right-4 bottom-20 w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-full shadow-lg flex items-center justify-center z-20"
          >
            <Layers size={20} />
          </button>

          {showPrototypeMenu && (
            <>
              <div 
                className="fixed inset-0 bg-black/20 z-20"
                onClick={() => setShowPrototypeMenu(false)}
              />
              <div className="fixed right-4 bottom-36 bg-white rounded-xl shadow-xl border border-slate-200 z-30 w-48 overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                  <span className="text-xs font-bold text-slate-600">选择详情页原型</span>
                </div>
                {[
                  { id: 'prototype1', name: '方案一', desc: '状态流可视化' },
                  { id: 'prototype2', name: '方案二', desc: '状态分组+折叠' },
                  { id: 'prototype3', name: '方案三', desc: '时间线+单据' },
                  { id: 'prototype4', name: '方案四', desc: '卡片+快捷操作' },
                ].map((proto) => (
                  <button
                    key={proto.id}
                    onClick={() => {
                      onPrototypeClick(proto.id);
                      setShowPrototypeMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                  >
                    <div className="text-sm font-bold text-slate-800">{proto.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{proto.desc}</div>
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )} */}
    </div>
  );
}